import React, { useCallback, useState } from "react";
import { kebabCase } from "lodash";
import styled from "styled-components";
import { Spacing } from "@glossy/spacings";
import { useCopyToClipboard, useTimeoutFn } from "@lkf/hooks";
import Icon from "@ui-kit/icon";
import Tooltip from "@ui-kit/tooltip";

const getUrlWithHash = (href: string, hash: string) => {
  const parts = href.split("#");
  const partsWithoutAnchor = parts.length > 2 ? parts.slice(0, -1) : parts;
  return [...partsWithoutAnchor, hash].join("#");
};

const AnchorWrapper = styled.div`
  position: absolute;
  height: 100%;
  top: 0;
  left: calc(-16px - ${Spacing.Micro});
  padding-right: ${Spacing.Micro}; /* Fill spacing between Root for continuous hovering */
  pointer-events: bounding-box; /* Trigger hover event also when hidden */
  box-sizing: content-box;
`;

const Anchor = styled(Icon)`
  opacity: 0;
  transform: translate(8px, 0);
  transition: opacity 0.2s ease 0s, transform 0.2s ease 0s;
  cursor: pointer;
  &:hover {
    opacity: 1;
    transform: none;
  }
`;

const Root = styled.div`
  position: relative;
  &:hover ${Anchor} {
    opacity: 1;
    transform: none;
  }
`;

interface IProps {
  // Unique string to generate the anchor hash from
  id: string;
  children?: React.ReactNode;
}

const useAnchorWithTooltip = (id: string) => {
  const anchorHash = kebabCase(id);
  const [, copyToClipboard] = useCopyToClipboard();
  const defaultTooltipLabel = "Copy link to heading";
  const [tooltipLabel, setTooltipLabel] = useState(defaultTooltipLabel);
  const [, , willResetLabel] = useTimeoutFn(
    () => setTooltipLabel(defaultTooltipLabel),
    2000
  );

  const handleClick = useCallback(() => {
    copyToClipboard(getUrlWithHash(window.location.href, anchorHash));
    setTooltipLabel("Copied!");
    willResetLabel();
  }, [anchorHash, copyToClipboard, setTooltipLabel, willResetLabel]);

  return { tooltipLabel, anchorHash, handleClick };
};

const UiKitAnchor = ({ id, children }: IProps) => {
  const { tooltipLabel, anchorHash, handleClick } = useAnchorWithTooltip(id);

  return (
    <Root id={anchorHash}>
      <AnchorWrapper>
        <Tooltip
          tooltip={tooltipLabel}
          placement="top"
          onClick={(e) => e.preventDefault()}
          onMouseDown={(e) => e.preventDefault()}
        >
          <div onClick={handleClick}>
            <Anchor glyph="link" />
          </div>
        </Tooltip>
      </AnchorWrapper>
      {children}
    </Root>
  );
};

export default UiKitAnchor;
